/**
 * evalDispositionBridge — the typed window-event seam between the marking
 * surface (StaffTechnicalPanel, the left pane) and the right-pane disposition
 * tabs (AUO disposition + Markups), per the build spec §3.
 *
 * WHY A BRIDGE (not a refactor). StaffTechnicalPanel (~3.2k lines) owns the
 * verified marking checkpoint and is the SINGLE source of truth for marks +
 * the save store. The v3.5 design puts the disposition lists on the RIGHT.
 * Rather than decompose the panel (high risk), we reuse the same window-event
 * pattern the shell already uses (`evalws:save`, `evalws:dirty-change`):
 *
 *   panel  ──emitMarks/ emitMarkups/ emitMarkSelected──▶  right-pane lists
 *   lists  ──requestSelectMark──▶  panel (highlight that marker on the map)
 *
 * The right-pane lists render whatever they receive and own NO truth. Until the
 * panel emit is wired (build Phase B/D), the lists show a calm empty state.
 *
 * PURE module: types + thin emit/subscribe helpers. No React, no Firebase.
 */
import type { AuoPaletteItem } from './auoPalette';

/* ───────────────────────── view models (right-pane only) ───────────────────────── */

/** One row of the unified AUO disposition list (doors first, then AUOs). */
export interface DispoItem {
  /** stable marker key (the panel's mark id) — the link to the map marker. */
  key: string;
  /** doors and AUOs share ONE numbered sequence; doors take the first numbers. */
  kind: 'door' | 'auo';
  /** 1-based number in the unified sequence (assigned by the emitter). */
  seqNo: number;
  /** display name, e.g. "Main door" / "Toilet". */
  name: string;
  /** type label, e.g. "Main door" / "Stair door" / "Toilet". */
  typeLabel: string;
  /** doors → pada code/range (e.g. "N3–N4"); AUOs → zone(s) (e.g. "SW"). */
  padaOrZone: string;
  /** AUO review status / verdict label (doors omit). Free string — the list
   *  maps known values (proposed/confirmed/rejected/favourable/needs remedy/
   *  review) to a chip class and falls back gracefully. */
  status?: string;
  /** the principal/main door (exactly one per floor). */
  principal?: boolean;
  /** doors only — the raw engine type (main_door / secondary_door / internal_entry
   *  / staircase_entry / balcony_opening …) so the edit card can preselect it. */
  rawType?: string;
  /** doors only — the FULL grid-derived pada list this door covers (e.g.
   *  ['N3','N4']), so the door card can resolve ONE entrance reading per pada
   *  (the report composer's per-pada rule). `padaOrZone` above is the joined
   *  display range; this carries the individual padas the reading iterates. */
  padas?: string[];
  /** AUOs only — the RAW staff-typed name (may be '' before naming). `name` above
   *  carries the display fallback ("AUO"); this carries the editable truth so the
   *  inline NAME input shows exactly what is stored. */
  auoName?: string;
  /** AUOs only — the full derived zone set (e.g. ['SW','WSW']), so the edit card
   *  can show the grid-derived zone read-only and render one engine row per zone. */
  zones?: string[];
  /** AUOs only — the engine's per-covered-zone disposition rows (once fetched via
   *  the suggest round-trip). One row per (mark × covered zone) — the preview's
   *  "name → zone → engine ruling" contract. Absent until the engine has answered. */
  suggestions?: AuoDispoRow[];
}

/** One engine-disposition row for an AUO, per covered zone — the inline ruling the
 *  naming flow shows (preview step 3). Mirrors the `avbp_auo_suggest` fixture shape
 *  the panel already fetches (`AuoSuggestion`), narrowed to what the row renders. */
export interface AuoDispoRow {
  /** the 16-zone this ruling is FOR. */
  zone: string;
  /** 'favourable' | 'neutral' | 'caution' | 'critical' | 'unrated' | null. */
  disposition: string | null;
  /** 0..3. */
  severity: number;
  /** 'critical' | 'remedy' | 'ok' | 'unspecified'. */
  severityLabel: string;
  /** the staff-facing effect sentence. */
  reason: string;
  /** false → the name won't normalise; the engine would skip it (the row warns). */
  recognised: boolean;
  /** the CANONICAL AUO key the engine resolved this name to (needed to address the
   *  override doc id when an admin corrects this cell). null when unrecognised. */
  normalised?: string | null;
  /** the verbatim effect text + remedy for this cell (the editor's start values). */
  effect?: string | null;
  management?: string | null;
  /** the owner's corrective colour NAME + optional metal for this cell (e.g. "Blue",
   *  "Off White"). Rendered as a swatch in BOTH surfaces via `swatchCss`. */
  corrective_colour?: string | null;
  corrective_metal?: string | null;
  /** provenance: 'vastu_xls' | 'db-miss' | 'override' | null. */
  source?: string | null;
  /** when source==='override', which layer it came from — 'project' (this project
   *  only) | 'global' (all reports). Drives the card's "Corrected for this project"
   *  vs "Corrected everywhere" label. */
  overrideScope?: 'project' | 'global' | null;
}

/** An edit/delete/confirm action requested from a disposition row (#4–#6). The
 *  marking surface resolves the key to a door, AUO, or markup and calls its
 *  existing op. */
export interface MarkAction {
  key: string;
  action:
    | 'delete'
    | 'confirm'
    | 'reject'
    | 'reopen'
    | 'set-door-type'
    | 'set-principal'
    /** AUO naming flow — set the free-text name, then fetch the engine ruling. */
    | 'set-auo-name'
    /** AUO palette pick — set the name AND its derived type ATOMICALLY (one patch,
     *  so the second setter never clobbers the first off a stale closure). */
    | 'set-auo-name-type'
    /** AUO zones override — replace the grid-derived zone set (the "edit zones"
     *  affordance). Zones read off the grid by default; this is the rare override. */
    | 'set-auo-zones'
    /** AUO inline-add — a free-typed name with no palette match → flow it to the
     *  pending collection for admin/Pitra promotion (best-effort, never blocks). */
    | 'record-pending-auo'
    | 'suggest'
    /** ADMIN-only — correct one (AUO × zone) ruling (label / verbatim effect /
     *  remedy) and SAVE it to the reference library at the chosen `scope`. */
    | 'correct-disposition'
    /** markup inline edit — set the free note and/or the custom label. */
    | 'set-markup-note'
    | 'set-markup-label';
  /** payload for set-door-type (raw engine type), set-auo-name / set-auo-name-type
   *  / record-pending-auo (the name), set-markup-note (the note) and
   *  set-markup-label (the label). */
  value?: string;
  /** set-auo-name-type only — the AUO `type` derived from the picked palette item
   *  (Activity / Utility / Object / Colour), travels alongside `value` (the name). */
  auoType?: string;
  /** set-auo-zones only — the overridden 16-zone set for this AUO. */
  zones?: string[];
  /* ── correct-disposition payload (the admin override loop) ── */
  /** the 16-zone whose ruling is being corrected. */
  zone?: string;
  /** the corrected disposition label (favourable/neutral/caution/critical). */
  disposition?: string;
  /** the owner's verbatim effect text (the ground-truth ruling). */
  effect?: string;
  /** the remedy / management text, or null. */
  management?: string | null;
  /** which layer to write to — 'project' (this project only) | 'global' (all reports). */
  scope?: 'project' | 'global';
}

/** Session-derived workspace context the right pane needs but does not own: the
 *  caller's admin flag (gates the disposition editor — MUST come from the real
 *  session, never hardcoded) and the project in scope (enables "this project only").
 *  Emitted by the marking surface (StaffTechnicalPanel), which holds the auth/route. */
export interface EvalContext {
  isAdmin: boolean;
  projectId: string | null;
}

/** One placed markup annotation (its OWN disposition, never in the AUO list). */
export interface MarkupItem {
  /** stable markup id — the link to the map annotation. */
  id: string;
  type: 'tick' | 'cross' | 'question' | 'cloud' | 'mirror' | 'arrow' | 'text';
  /** short label, e.g. "Revision cloud" or the note text. */
  label: string;
  /** optional free note carried on the annotation. */
  note?: string;
  /** the 16-zone(s) / padas this markup's geometry falls over, DERIVED off the grid
   *  by the emitter (a markup has NO disposition, but it does cover a zone set — the
   *  expand-detail shows it). Empty when the markup is off-grid or no grid exists. */
  zones?: string[];
  /** 1-based number in the SINGLE unified sequence (owner-confirmed): entrances →
   *  AUOs → markups, unbroken. Seeded at (door + AUO count) by the emitter; the
   *  list renders this verbatim rather than its own array index. */
  seqNo: number;
}

/* ───────────────────────── event names (one place) ───────────────────────── */

export const EVAL_EVENTS = {
  /** panel → lists: the full door+AUO disposition (in unified order). */
  marksChange: 'evalws:marks-change',
  /** panel → lists: the full markup list. */
  markupsChange: 'evalws:markups-change',
  /** panel → lists: a marker was selected on the map (highlight its row). */
  markSelected: 'evalws:mark-selected',
  /** lists → panel: select this marker on the map (a row was clicked). */
  selectMark: 'evalws:select-mark',
  /** lists → panel: edit/delete/confirm a marker (#4–#6). */
  markAction: 'evalws:mark-action',
  /** panel → lists: the AUO name palette (so the right-pane naming card can run
   *  the SAME searchable picker the left list uses). */
  paletteChange: 'evalws:palette-change',
  /** toolbar → panel: undo / redo the last mark mutation (bounded history). */
  requestUndo: 'evalws:request-undo',
  requestRedo: 'evalws:request-redo',
  /** panel → toolbar: whether undo / redo are currently available (button enable). */
  historyChange: 'evalws:history-change',
  /** panel → lists: session context (admin flag + project) for the right pane. */
  contextChange: 'evalws:context-change',
} as const;

/** Whether the bounded mark-history can step back / forward right now. */
export interface HistoryState {
  canUndo: boolean;
  canRedo: boolean;
}

/* ───────────────────────── emit (panel side) ───────────────────────── */

export function emitMarks(items: DispoItem[]): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.marksChange, { detail: { items } }));
}

export function emitMarkups(items: MarkupItem[]): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.markupsChange, { detail: { items } }));
}

export function emitMarkSelected(key: string | null): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.markSelected, { detail: { key } }));
}

export function emitPalette(items: AuoPaletteItem[]): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.paletteChange, { detail: { items } }));
}

export function emitHistory(state: HistoryState): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.historyChange, { detail: state }));
}

export function emitContext(ctx: EvalContext): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.contextChange, { detail: ctx }));
}

/* ───────────────────────── request (list side) ───────────────────────── */

export function requestSelectMark(key: string): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.selectMark, { detail: { key } }));
}

export function requestMarkAction(a: MarkAction): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.markAction, { detail: a }));
}

export function requestUndo(): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.requestUndo));
}

export function requestRedo(): void {
  window.dispatchEvent(new CustomEvent(EVAL_EVENTS.requestRedo));
}

/** Panel side: subscribe to edit/delete/confirm actions from the lists. */
export function subscribeMarkAction(cb: (a: MarkAction) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as MarkAction | undefined;
    if (d && typeof d.key === 'string' && d.action) cb(d);
  };
  window.addEventListener(EVAL_EVENTS.markAction, h);
  return () => window.removeEventListener(EVAL_EVENTS.markAction, h);
}

/* ───────────────────────── subscribe helpers ───────────────────────── */

/** Subscribe to the door+AUO disposition. Returns an unsubscribe fn. */
export function subscribeMarks(cb: (items: DispoItem[]) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as { items?: DispoItem[] } | undefined;
    cb(Array.isArray(d?.items) ? d!.items! : []);
  };
  window.addEventListener(EVAL_EVENTS.marksChange, h);
  return () => window.removeEventListener(EVAL_EVENTS.marksChange, h);
}

/** Subscribe to the markup disposition. Returns an unsubscribe fn. */
export function subscribeMarkups(cb: (items: MarkupItem[]) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as { items?: MarkupItem[] } | undefined;
    cb(Array.isArray(d?.items) ? d!.items! : []);
  };
  window.addEventListener(EVAL_EVENTS.markupsChange, h);
  return () => window.removeEventListener(EVAL_EVENTS.markupsChange, h);
}

/** Subscribe to "a marker was selected on the map". Returns an unsubscribe fn. */
export function subscribeMarkSelected(cb: (key: string | null) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as { key?: string | null } | undefined;
    cb(d?.key ?? null);
  };
  window.addEventListener(EVAL_EVENTS.markSelected, h);
  return () => window.removeEventListener(EVAL_EVENTS.markSelected, h);
}

/** Panel side: subscribe to "select this marker" requests from the list. */
export function subscribeSelectMark(cb: (key: string) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as { key?: string } | undefined;
    if (typeof d?.key === 'string') cb(d.key);
  };
  window.addEventListener(EVAL_EVENTS.selectMark, h);
  return () => window.removeEventListener(EVAL_EVENTS.selectMark, h);
}

/** List side: subscribe to the session context (admin flag + project in scope). */
export function subscribeContext(cb: (ctx: EvalContext) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as EvalContext | undefined;
    cb({ isAdmin: !!d?.isAdmin, projectId: d?.projectId ?? null });
  };
  window.addEventListener(EVAL_EVENTS.contextChange, h);
  return () => window.removeEventListener(EVAL_EVENTS.contextChange, h);
}

/** List side: subscribe to the AUO name palette emitted by the panel. */
export function subscribePalette(cb: (items: AuoPaletteItem[]) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as { items?: AuoPaletteItem[] } | undefined;
    cb(Array.isArray(d?.items) ? d!.items! : []);
  };
  window.addEventListener(EVAL_EVENTS.paletteChange, h);
  return () => window.removeEventListener(EVAL_EVENTS.paletteChange, h);
}

/** Panel side: subscribe to undo requests from the toolbar. */
export function subscribeUndo(cb: () => void): () => void {
  const h = () => cb();
  window.addEventListener(EVAL_EVENTS.requestUndo, h);
  return () => window.removeEventListener(EVAL_EVENTS.requestUndo, h);
}

/** Panel side: subscribe to redo requests from the toolbar. */
export function subscribeRedo(cb: () => void): () => void {
  const h = () => cb();
  window.addEventListener(EVAL_EVENTS.requestRedo, h);
  return () => window.removeEventListener(EVAL_EVENTS.requestRedo, h);
}

/** Toolbar side: subscribe to "can undo / can redo" so the controls enable/disable. */
export function subscribeHistory(cb: (state: HistoryState) => void): () => void {
  const h = (e: Event) => {
    const d = (e as CustomEvent).detail as HistoryState | undefined;
    cb({ canUndo: !!d?.canUndo, canRedo: !!d?.canRedo });
  };
  window.addEventListener(EVAL_EVENTS.historyChange, h);
  return () => window.removeEventListener(EVAL_EVENTS.historyChange, h);
}
